import { fill, t } from '../strings';
import { BULK_EDIT_MAX_ROWS, filterSelectionProblem } from './bulkEdit';

/**
 * 🔒 A list page's Select all N matching, as an action sends it (decision 7780, amending 1890; T3156):
 * the list read's own query, its search, tag, filters, built-in view and saved view's criteria, and
 * the ids the person unticked. It is never a list of every id (piece 4).
 */
export interface ListFilterSelection {
  query: string;
  exclusions: readonly string[];
}

/** What the selection route answers: the matching ids less the exclusions, and how many. */
export interface ListSelectionAnswer {
  ids: string[];
  total: number;
}

/** 🔨 The most rows one resolution names, the most a bulk edit takes in one request (decision 7780). */
export const LIST_SELECTION_MAX_ROWS = BULK_EDIT_MAX_ROWS;

/** The route that resolves a filter selection on the server, gated as the list's own read (decision 7780). */
export const listSelectionPath = (recordType: string): string => `/pages/list/${encodeURIComponent(recordType)}/selection`;

/** 🔒 The keys of a list read that order or cut the page and change no row of the set, so a selection drops them. */
const PAGE_ONLY_KEYS = ['page', 'pageSize', 'sort', 'sortDirection'] as const;

/** The list read's query as a selection carries it: every filter the read names, and nothing that only orders or cuts the page. */
export function listSelectionQuery(readQuery: string): string {
  const query = new URLSearchParams(readQuery.startsWith('?') ? readQuery.slice(1) : readQuery);
  for (const key of PAGE_ONLY_KEYS) query.delete(key);
  return query.toString();
}

/** The body the selection route takes, checked once: a query in text and a list of ids, or the sentence refusing it. */
export function listSelectionProblem(body: unknown): string | null {
  return filterSelectionProblem(body);
}

/** 🔒 The sentence a resolution past the ceiling is refused with, naming how many rows it matched (decisions 7780, 7870). */
export function listSelectionTooManySentence(matched: number): string {
  return fill(t('list.selection.tooMany'), { count: String(matched), max: String(LIST_SELECTION_MAX_ROWS) });
}

/** 🔒 The cap in words, which Edit Selected's head reads over a filter selection (decision 7870). */
export function listSelectionCapSentence(): string {
  return fill(t('list.selection.cap'), { max: String(LIST_SELECTION_MAX_ROWS) });
}
